// Frame extraction for the dubbing timeline's media track: WebCodecs over data
// read through HTTP Range, so only keyframes travel instead of the whole file.
//
// The waveform deliberately does NOT go through here. Measured on a real
// dubbing source: ffmpeg interleaves packets by DTS (audio 387B / video 6.8KB,
// close to 1:1), so pulling the audio track alone through Range still drags
// ~77% of the file along, and sampling it sparsely produces an envelope that
// correlates 0.015 with the truth — it invents silences that are not there.
// Waveform stays on Web Audio (services/audioWaveform); this module only lends
// it the IndexedDB cache.
//
// One file, matching the flat convention of src/services: every section below
// shares the range reader, and splitting them would spawn interfaces nobody
// outside this file consumes.

// ------------------------------------------------------------- capability --

/**
 * Whether VideoDecoder is usable. Without it the caller falls back to the old
 * <video> seek loop. Safari before 16.4 and Firefox before 130 land here.
 */
export const canUseWebCodecs = () =>
  typeof window !== 'undefined' && typeof window.VideoDecoder === 'function';

// ---------------------------------------------------------------- planning --

/**
 * A cache key that survives presigning. S3 urls carry a signature that changes
 * per request, so keying on the full url would miss every time; origin +
 * pathname identifies the object itself. Pure.
 */
export const normalizeCacheKey = (url) => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (error) {
    return url;
  }
};

const BOX_HEADER_BYTES = 8;

/**
 * Locate the `moov` box by walking the top-level box chain in `headBuffer` (the
 * first 64KB is ample). Returns `{ start, size }`, or null when the chain does
 * not parse.
 *
 * Dubbing sources are usually NOT faststart — the measured layout is
 * ftyp/free/mdat/moov with a 169MB mdat. Once the walk steps past mdat it falls
 * outside headBuffer, so the next box's type is unreadable; but its offset is
 * known, and reading from there to EOF is certain to cover moov (mp4box ignores
 * whatever else tags along). Beats feeding 1MB chunks until moov turns up:
 * measured 638KB in 2 requests against 5.2MB in 6.
 *
 * Pure.
 */
export const locateMoov = (headBuffer, totalSize) => {
  const dv = new DataView(headBuffer);
  let offset = 0;

  while (offset + BOX_HEADER_BYTES <= headBuffer.byteLength) {
    let size = dv.getUint32(offset);
    const type = String.fromCharCode(
      dv.getUint8(offset + 4),
      dv.getUint8(offset + 5),
      dv.getUint8(offset + 6),
      dv.getUint8(offset + 7),
    );
    let headerBytes = BOX_HEADER_BYTES;

    if (size === 1) {
      // size === 1 means a 64-bit largesize follows — used by mdat past 4GB.
      if (offset + 16 > headBuffer.byteLength) return null;
      size = dv.getUint32(offset + 8) * 2 ** 32 + dv.getUint32(offset + 12);
      headerBytes = 16;
    } else if (size === 0) {
      // size === 0 means the box runs to the end of the file.
      size = totalSize - offset;
    }

    if (size < headerBytes) return null;
    if (type === 'moov') return { start: offset, size };

    const next = offset + size;
    if (next >= totalSize) return null;
    if (next + BOX_HEADER_BYTES > headBuffer.byteLength) {
      return { start: next, size: totalSize - next };
    }
    offset = next;
  }
  return null;
};

// ------------------------------------------------------------------ cache --

const DB_NAME = 'timeline-media';
const DB_VERSION = 1;
const STORE = 'extracts';

// Bump when the extraction changes, so stale results are not reused.
// v2: entries carry each frame's timestamp.
// v3: keyframes that decode flat are replaced by a neighbour.
// v4: a flat keyframe with nothing to swap in is dropped, and near-black /
//     near-white frames now count as flat (v3 entries hold those as black
//     tiles, so they have to be re-extracted rather than reused).
const ENGINE_VERSION = 4;

// Per kind, because the kinds are nothing alike in size: a `strip` holds one
// tile per second of video (3.5MB for 799 of them, so it grows with duration), a
// `card` frame measured 11-28KB, and `peaks` is at most 1600 numbers. One shared
// cap of 8 made them evict each other — measured on a ten-project account, seven
// of them video, the home page still downloaded 10.75MB on a SECOND visit
// because two projects never fit, and one visit to a timeline could clear every
// thumbnail on the page.
//
// Every number here is above the 8 it replaces, so no kind holds less than it
// could before. They are generous because the measured cost is not: 200 cards is
// 3.4MB, 12 strips of a 13-minute source is 42MB, and the origin quota measured
// 10.2GB. Storage was never the thing in short supply.
const MAX_ENTRIES = { card: 200, strip: 12, peaks: 64 };

// For a kind nothing above accounts for — the old shared cap, which is the
// conservative thing to do with an entry whose size is unknown.
const FALLBACK_MAX_ENTRIES = 8;

/**
 * The kind a stored key belongs to.
 *
 * Read off the key rather than a field on the record, so trimming by kind needs
 * neither a DB_VERSION bump nor a migration — and a migration here would have to
 * load every payload to rewrite it, which is the one thing walking the index
 * exists to avoid. Counted from the end because a pathname may hold a `|`. Pure.
 */
const kindOf = (key) => key.split('|').slice(-2)[0] || '';

const openDb = () =>
  new Promise((resolve, reject) => {
    // Private mode blocks IndexedDB — that means no cache, not an error.
    if (typeof indexedDB === 'undefined') {
      reject(new Error('no indexeddb'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        // Needed to trim by age without loading every entry's payload.
        store.createIndex('savedAt', 'savedAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error('indexeddb open failed'));
  });

const withStore = async (mode, run) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const box = run(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(box.value);
      tx.onerror = () => reject(new Error('indexeddb tx failed'));
    });
  } finally {
    db.close();
  }
};

const cacheKeyFor = (url, kind) =>
  `${normalizeCacheKey(url)}|${kind}|v${ENGINE_VERSION}`;

/** A stored entry, or null on a miss or when IndexedDB is unavailable. */
export const readCache = async (url, kind) => {
  try {
    return await withStore('readonly', (store) => {
      const request = store.get(cacheKeyFor(url, kind));
      const box = { value: null };
      request.onsuccess = () => {
        box.value = request.result ? request.result.payload : null;
      };
      return box;
    });
  } catch (error) {
    return null;
  }
};

/**
 * Store an entry and trim the oldest ones OF THAT KIND. Swallows failures — a
 * broken cache must not break the UI.
 *
 * Trimming walks the `savedAt` index with a KEY cursor rather than calling
 * store.getAll(), which would pull every entry's payload into memory (a handful
 * of strips is tens of megabytes) just to read one number. Counting happens in
 * that same walk instead of through store.count(), which cannot count one kind:
 * the walk is what keeps the kinds from evicting each other.
 */
export const writeCache = async (url, kind, payload) => {
  try {
    await withStore('readwrite', (store) => {
      store.put({ key: cacheKeyFor(url, kind), savedAt: Date.now(), payload });
      const limit = MAX_ENTRIES[kind] || FALLBACK_MAX_ENTRIES;

      // Cheap way out first: if the WHOLE store fits this kind's limit, then
      // this kind does too, and there is nothing to walk. Worth keeping because
      // the timeline saves its strip every few seconds while one fills, and
      // those writes should not each drag a cursor over every key.
      const countRequest = store.count();
      countRequest.onsuccess = () => {
        if (countRequest.result <= limit) return;

        // Ascending by savedAt, so the front of this list is what goes first.
        // The put above belongs to this transaction, so the new entry is counted
        // with the rest rather than letting the store sit one over the limit
        // until the next write.
        const ofKind = [];
        const cursorRequest = store.index('savedAt').openKeyCursor();
        cursorRequest.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            if (kindOf(cursor.primaryKey) === kind)
              ofKind.push(cursor.primaryKey);
            cursor.continue();
            return;
          }
          // The walk is over: everything past the limit is the oldest of them.
          const excess = ofKind.length - limit;
          for (let at = 0; at < excess; at += 1) store.delete(ofKind[at]);
        };
      };
      return { value: undefined };
    });
  } catch (error) {
    // No cache just means slower next time.
  }
};

// ------------------------------------------------------------ local source --

// The uploader's machine already holds the exact bytes the timeline is about to
// pull back down: the File picked in the create-project popup. Registered here
// under the same key the cache uses (origin + pathname), so the presigned
// variants of that url the project serves later still match — and every range
// read below is served from disk instead of the network.
//
// Session-only, and deliberately so. A File is a handle to the file on disk,
// not a copy — holding a few costs nothing, while persisting one to IndexedDB
// would copy the whole video into the origin's storage. The bytes are only
// needed until the strip and peaks finish extracting (seconds, read locally);
// what a later visit needs is those small extracts, and the cache below already
// keeps them. A reload before they finish just falls back to the network.
const MAX_LOCAL_SOURCES = 3;
const localSources = new Map();

/**
 * Remember that `url` was uploaded from `file` on this machine. From then on,
 * readers of that url (any presigned variant) get the local bytes.
 */
export const registerLocalSource = (url, file) => {
  const key = normalizeCacheKey(url);
  if (!key || !file) return;
  localSources.delete(key);
  localSources.set(key, file);
  // Insertion order is age; the map never outgrows the handful it is for.
  while (localSources.size > MAX_LOCAL_SOURCES) {
    localSources.delete(localSources.keys().next().value);
  }
};

/** The local File whose upload produced `url`, or null. */
export const localSourceOf = (url) => {
  const key = normalizeCacheKey(url);
  return (key && localSources.get(key)) || null;
};

/**
 * Forget `url`'s local file — for a reader that found the handle dead (the
 * file on disk moved or changed since it was picked). Later reads go back to
 * the network instead of failing the same way forever.
 */
export const dropLocalSource = (url) => {
  localSources.delete(normalizeCacheKey(url));
};

// ------------------------------------------------------------ range reader --

// Read from the network rather than reuse the HTTP cache. An S3 response
// fetched WITHOUT an Origin header carries neither Access-Control-Allow-Origin
// nor Vary (verified with curl), so if that copy lands in the cache first a
// later CORS fetch matches it and fails. Every consumer of the video url now
// sets crossOrigin, so the trap is dormant — this stays as a cheap guard, since
// nothing else fetches these ranges anyway.
const FETCH_INIT = { cache: 'reload', credentials: 'omit' };

// Ample for walking the top-level box chain.
const HEAD_PROBE_BYTES = 64 * 1024;

// Every read here is small — a keyframe, a GOP, the moov box — so taking longer
// than this is a stall, not slowness.
//
// Nothing used to bound them. The only signal passed in is the caller's, and
// that is aborted on unmount, so a connection that went quiet held its promise
// for the life of the page: the lead pass parked on `Promise.all`, the fill loop
// parked on the lead pass, and the filmstrip froze half-drawn with no way back.
// A stall now surfaces as a failure, which is something the caller can retry.
const REQUEST_TIMEOUT_MS = 30000;

/**
 * A signal that aborts when `signal` does OR after `ms`, plus the cleanup for
 * its timer. Hand-rolled rather than `AbortSignal.any`/`AbortSignal.timeout`:
 * this file is also the path Safari and Firefox take, and those are recent
 * additions there.
 */
const withTimeout = (signal, ms) => {
  const controller = new AbortController();
  const stop = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', stop, { once: true });
  }
  const timer = setTimeout(stop, ms);
  return {
    signal: controller.signal,
    /** Give up on this request now — for a caller that only wanted the header. */
    abort: stop,
    done: () => {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', stop);
    },
  };
};

/**
 * Total size of the file. Content-Range is unreadable (S3 sends no
 * Access-Control-Expose-Headers) but Content-Length is CORS-safelisted, so open
 * a plain GET, read the header, and abort before the body arrives. HEAD is not
 * an option: S3 allows only GET/PUT/POST, so a preflight would fail. Returns 0
 * when the size cannot be established.
 */
export const getTotalSize = async (url, signal) => {
  const local = localSourceOf(url);
  if (local) return local.size;
  const guard = withTimeout(signal, REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { ...FETCH_INIT, signal: guard.signal });
    const size = Number(res.headers.get('content-length'));
    return Number.isFinite(size) && size > 0 ? size : 0;
  } catch (error) {
    return 0;
  } finally {
    // Dropped either way: only the header was ever wanted, never the body.
    guard.abort();
    guard.done();
  }
};

/**
 * One byte range, `end` inclusive. Throws when the server ignores the Range
 * header and answers 200 — being handed the whole file is precisely what this
 * pipeline exists to avoid. `signal` cancels a request still in flight, so
 * switching projects does not leave downloads stacking up.
 */
export const fetchRange = async (url, start, end, signal) => {
  const local = localSourceOf(url);
  if (local) {
    try {
      // `end` is inclusive like the Range header; slice's end is not.
      return await local.slice(start, end + 1).arrayBuffer();
    } catch (error) {
      // The file on disk moved or changed since it was picked, so the copy is
      // dead — every later read would fail the same way. Back to the network
      // for this and everything after.
      dropLocalSource(url);
    }
  }
  const guard = withTimeout(signal, REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      ...FETCH_INIT,
      signal: guard.signal,
      headers: { Range: `bytes=${start}-${end}` },
    });
    if (res.status !== 206) throw new Error('range not supported');
    // Awaited inside the guard, so a body that stops arriving part way through
    // is a timeout too rather than a promise nobody ever settles.
    return await res.arrayBuffer();
  } finally {
    guard.done();
  }
};

// ------------------------------------------------------------------ demux --

/**
 * mp4box ships as CommonJS with named exports and no default. Webpack's interop
 * may still surface a `default` holding module.exports, so accept either.
 */
const loadMp4Box = async () => {
  const mod = await import('mp4box');
  if (typeof mod.createFile === 'function') return mod;
  if (mod.default && typeof mod.default.createFile === 'function') {
    return mod.default;
  }
  throw new Error('mp4box unavailable');
};

/**
 * The codec configuration bytes (avcC / hvcC / ...) that VideoDecoder.configure
 * requires. Without them the decoder rejects every chunk. mp4box writes the box
 * including its 8-byte header, which has to come off.
 */
const readVideoDescription = (mp4box, file, trackId) => {
  const { DataStream } = mp4box;
  if (!DataStream) return undefined;

  const trak = file.moov.traks.find((t) => t.tkhd.track_id === trackId);
  if (!trak) return undefined;
  const entry = trak.mdia.minf.stbl.stsd.entries[0];
  const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
  if (!box) return undefined;

  const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
  box.write(stream);
  return new Uint8Array(stream.buffer, 8);
};

/**
 * Open an mp4/mov over HTTP Range and return its track info plus sample table,
 * without touching the media data. Reads 64KB to locate moov, then fetches
 * exactly moov. Throws when Range is unsupported, the box chain is broken, or
 * mp4box cannot read the file — the caller falls back to <video> in all cases.
 */
const openMp4 = async (url, signal) => {
  const mp4box = await loadMp4Box();
  const totalSize = await getTotalSize(url, signal);
  if (!totalSize) throw new Error('unknown size');

  const head = await fetchRange(url, 0, HEAD_PROBE_BYTES - 1, signal);
  const moov = locateMoov(head, totalSize);
  if (!moov) throw new Error('moov not found');

  const file = mp4box.createFile();
  const info = await new Promise((resolve, reject) => {
    file.onReady = resolve;
    file.onError = () => reject(new Error('mp4 parse failed'));

    head.fileStart = 0;
    file.appendBuffer(head);
    // A small faststart file may already be complete, in which case onReady has
    // fired and no second request is needed.
    if (file.moov) return;

    const end = Math.min(moov.start + moov.size - 1, totalSize - 1);
    fetchRange(url, moov.start, end, signal)
      .then((buffer) => {
        // eslint-disable-next-line no-param-reassign
        buffer.fileStart = moov.start;
        file.appendBuffer(buffer);
        file.flush();
        if (!file.moov) reject(new Error('moov not found'));
      })
      .catch(reject);
  });

  const videoTrack = info.tracks.find((track) => track.type === 'video');
  if (videoTrack) {
    videoTrack.description = readVideoDescription(mp4box, file, videoTrack.id);
  }

  return {
    totalSize,
    videoTrack,
    getSamples: (trackId) => file.getTrackSamplesInfo(trackId),
    close: () => file.flush(),
  };
};

// ------------------------------------------------------------------ frame --

const FRAME_JPEG_QUALITY = 0.8;

/**
 * A sample's composition time in whole microseconds — how a frame is named
 * everywhere WebCodecs is involved.
 *
 * One expression, because three places have to agree on it: the chunk handed to
 * the decoder, the set of frames worth drawing, and the caller's own note of
 * what it asked for. A rounding that differs between them means the decoder
 * hands back a frame nobody recognises. Pure.
 */
export const stampOf = (sample, timescale) =>
  Math.round((sample.cts / timescale) * 1e6);

/** The moment the player shows a sample, once the edit list is taken off. Pure. */
const secOf = (sample, timescale, offsetSec) =>
  sample.cts / timescale - offsetSec;

/**
 * Index of the frame closest in time to `seconds`.
 *
 * Nearest, not the last one at or before: keyframes are sparse and irregular,
 * so "the frame currently showing" can be most of a keyframe gap behind the
 * tile it sits under. Nearest halves the error. Pure.
 */
export const frameIndexAt = (times, seconds) => {
  if (!times || !times.length) return 0;
  let low = 0;
  let high = times.length - 1;
  let before = 0;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (times[mid] <= seconds) {
      before = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  const after = Math.min(before + 1, times.length - 1);
  return seconds - times[before] <= times[after] - seconds ? before : after;
};

/**
 * Seconds to subtract from a sample's `cts` to get the moment the player shows
 * it.
 *
 * `getTrackSamplesInfo` reports composition times in MEDIA time, but an edit
 * list shifts where media time lands on the presentation timeline. The measured
 * dubbing source carries `media_time: 1024` on a timescale of 15360 — every
 * frame placed without this is 66.7ms late, which is a fifth of a tile at full
 * zoom. An entry of -1 is an empty edit, so the first one that maps real media
 * sets the offset. Pure.
 */
export const mediaTimeOffsetSec = (track) => {
  const edit = (track?.edits || []).find((entry) => entry.media_time >= 0);
  if (!edit || !track.timescale) return 0;
  return edit.media_time / track.timescale;
};

// How many VideoDecoders may be alive at once, across every caller.
//
// A VideoDecoder is a driver resource, not a JS object, and nothing bounded them.
// `decodeChunk` opens one per chunk, and mediaFile hands each landed chunk to its
// listeners WITHOUT awaiting them before pumping the next fetch — so whenever
// decoding ran slower than downloading, which is the normal case on integrated
// graphics, the count grew to fetch-rate over decode-rate with no ceiling. The
// home page adds one per project card on top of that.
//
// Two rather than one so the keyframe lead pass, which holds its decoder open
// across network waits by design, cannot stall the chunk fill behind it.
const MAX_LIVE_DECODERS = 2;

let liveDecoders = 0;
const decoderQueue = [];

const acquireDecoder = () => {
  if (liveDecoders < MAX_LIVE_DECODERS) {
    liveDecoders += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => decoderQueue.push(resolve));
};

// The slot is handed straight to the next waiter rather than counted down and up
// again, so a queue can never be left holding slots nobody is using.
const releaseDecoder = () => {
  const next = decoderQueue.shift();
  if (next) next();
  else liveDecoders = Math.max(0, liveDecoders - 1);
};

// Canvases to keep for re-use. A frame is drawn and then read back through
// toBlob, which is asynchronous, so the canvas being read must not be drawn over
// — hence one per encode in flight rather than one shared. Kept in a pool rather
// than made per frame, which is what this used to do: a 90-minute video meant
// 5400 canvas allocations, and since each was thrown away after a single
// readback, Chrome's heuristic — which drops a canvas off the GPU only AFTER its
// first readback — never got to apply to any of them. Every frame therefore paid
// a GPU surface, a frame import and a pipeline-stalling ReadPixels.
const CANVAS_POOL_MAX = 4;
const canvasPool = [];

// `willReadFrequently` keeps the surface on the CPU, which is what this workload
// wants: every frame drawn here is read straight back out as a JPEG, so there is
// nothing to gain from the GPU and a readback to pay for. `alpha: false` skips
// the premultiply work for pixels JPEG cannot carry anyway.
const takeCanvas = (width, height) => {
  const canvas = canvasPool.pop() || document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const giveBackCanvas = (canvas) => {
  if (canvasPool.length < CANVAS_POOL_MAX) canvasPool.push(canvas);
};

// How much work may sit in the codec before more is queued. Chrome saturates
// between one and eight chunks; past that they pile up in its control-message
// queue. Sending thousands at once can make the decoder close or fail outright,
// and the loop that sends them holds the thread, so no output callback runs, no
// VideoFrame gets closed, and a hardware decoder's output buffers — which, unlike
// a software decoder's, are finite — fill up behind it.
const MAX_DECODE_QUEUE = 8;
// Waiting on `dequeue` alone would hang on a decoder that has stopped draining,
// so each wait is also timed, and the number of waits is bounded. Past that it
// queues anyway, which is what it always did.
const DEQUEUE_TIMEOUT_MS = 500;
const MAX_DEQUEUE_WAITS = 20;

/**
 * A decoder fed samples in decode order, handing out the ones asked for as
 * thumbnails.
 *
 * Only the wanted frames are drawn. Reaching a frame between keyframes means
 * decoding every frame back to the keyframe before it, and those are reference
 * material only — measured, a screenful at full zoom walks 195 frames to keep
 * 18. Closing the rest straight out of the decoder, without allocating a canvas,
 * is what makes that walk cost 79ms.
 *
 * Samples MUST arrive in decode order (`dts`). A video with B-frames stores them
 * in a different order from composition, and one reordered chunk corrupts every
 * frame after it. A file read front to back is already in decode order.
 *
 * `finish()` resolves once every queued chunk has been decoded and encoded.
 */
const createFrameDecoder = ({
  track,
  height,
  aspect,
  offsetSec,
  wanted,
  onFrame,
}) => {
  const width = Math.round(height * aspect);
  let pending = 0;
  let flushed = false;
  let settle = () => {};
  let fail = () => {};
  const finished = new Promise((resolve, reject) => {
    settle = resolve;
    fail = reject;
  });
  const check = () => {
    if (flushed && pending === 0) settle();
  };

  const decoder = new window.VideoDecoder({
    output: (frame) => {
      const { timestamp } = frame;
      if (!wanted.has(timestamp)) {
        frame.close();
        return;
      }
      wanted.delete(timestamp);
      // Drawn and closed with nothing awaited in between. Holding a VideoFrame
      // open while waiting for anything — a free canvas included — is what
      // exhausts a hardware decoder's output buffers and stalls it.
      const canvas = takeCanvas(width, height);
      canvas
        .getContext('2d', { willReadFrequently: true, alpha: false })
        .drawImage(frame, 0, 0, width, height);
      frame.close();

      pending += 1;
      canvas.toBlob(
        (blob) => {
          if (blob) onFrame(timestamp / 1e6 - offsetSec, blob, timestamp);
          giveBackCanvas(canvas);
          pending -= 1;
          check();
        },
        'image/jpeg',
        FRAME_JPEG_QUALITY,
      );
    },
    error: () => fail(new Error('video decode failed')),
  });
  decoder.configure({
    codec: track.codec,
    codedWidth: track.video.width,
    codedHeight: track.video.height,
    description: track.description,
    // Favour handing frames back early over raw throughput: a strip that fills
    // in reads far quicker than one that appears at the end.
    optimizeForLatency: true,
  });

  // One wait for the codec to report progress. Prefers the `dequeue` event —
  // which exists so callers need not poll — and falls back to the timer on a
  // browser that does not fire it. Resolving on the timer is safe: the caller
  // re-tests the queue depth, so a spurious wake just costs one more loop.
  const waitForDequeue = () =>
    new Promise((resolve) => {
      let timer = 0;
      const done = () => {
        clearTimeout(timer);
        decoder.removeEventListener('dequeue', done);
        resolve();
      };
      timer = setTimeout(done, DEQUEUE_TIMEOUT_MS);
      decoder.addEventListener('dequeue', done, { once: true });
    });

  return {
    /**
     * Wait until there is room in the codec, then return. Call before `push`.
     *
     * Two things this buys. The queue stays bounded, which is what keeps the
     * decoder from closing or failing under thousands of chunks. And awaiting
     * hands the thread back, which is the only way the output callbacks above get
     * to run — so frames are closed and their buffers freed while the run is
     * still queueing, rather than all at the end.
     *
     * Bounded: past MAX_DEQUEUE_WAITS it queues regardless, and it returns at
     * once if the decoder is no longer configured, so a decoder that has died
     * cannot park the caller here.
     */
    ready: async () => {
      for (let waits = 0; waits < MAX_DEQUEUE_WAITS; waits += 1) {
        if (decoder.state !== 'configured') return;
        if (decoder.decodeQueueSize < MAX_DECODE_QUEUE) return;
        // eslint-disable-next-line no-await-in-loop
        await waitForDequeue();
      }
    },

    /** Queue one sample. `data` is its bytes. */
    push: (sample, data) => {
      decoder.decode(
        new window.EncodedVideoChunk({
          type: sample.is_sync ? 'key' : 'delta',
          timestamp: stampOf(sample, track.timescale),
          data,
        }),
      );
    },
    finish: async () => {
      await decoder.flush().catch(() => {});
      decoder.close();
      flushed = true;
      check();
      return finished;
    },

    /**
     * Let the decoder go WITHOUT draining it — for a pass that has been
     * abandoned and does not want its remaining frames.
     *
     * `close()` is the spec's "abort pending work and release resources", which
     * is exactly what an abandoned pass wants. `flush()` is the opposite: it
     * makes the decoder emit everything still queued, so every remaining frame
     * runs the output path — a decoded frame drawn into a canvas and read back
     * out — at the moment the caller is being torn down. Draining a pass whose
     * output is about to be thrown away is work for nothing at the worst moment
     * to be doing it.
     *
     * Idempotent, and safe on a decoder that has already errored (its state is
     * `closed` by then, and closing twice throws).
     */
    discard: () => {
      if (decoder.state !== 'closed') decoder.close();
      flushed = true;
      check();
    },
  };
};

/**
 * Read the sample table without touching the media data: 64KB to find `moov`,
 * then `moov` itself. Measured 638KB over two requests on a source whose moov
 * sits at the end.
 *
 * The stream that follows will fetch those bytes again as part of the whole
 * file. That is 0.4% of the download, and it is the price of knowing where every
 * frame is BEFORE the file has finished arriving — without it there is no strip
 * until the last byte lands.
 */
export const readSampleTable = async (url, signal) => {
  const media = await openMp4(url, signal);
  const track = media.videoTrack;
  if (!track) throw new Error('no video track');
  const samples = media.getSamples(track.id);
  media.close();
  if (!samples.length) throw new Error('no samples');
  const offsetSec = mediaTimeOffsetSec(track);
  // Decode order, which is also the order the file holds them in — so a sample
  // is ready exactly when the download has passed its last byte.
  const inOrder = [...samples].sort((a, b) => a.dts - b.dts);
  // The keyframes once, here. Chunk boundaries, the moments a coarse tile aims
  // at and the frames fetched ahead of the download are all the same list, and
  // three passes over 23941 samples to build it three times is the sort of thing
  // that quietly disagrees.
  const keyframes = inOrder.filter((sample) => sample.is_sync);
  return {
    track,
    totalSize: media.totalSize,
    samples: inOrder,
    keyframes,
    keyTimes: keyframes.map((sample) =>
      secOf(sample, track.timescale, offsetSec),
    ),
    aspect: track.video.width / track.video.height,
    videoDuration: track.duration / track.timescale,
    offsetSec,
  };
};

/**
 * Index of the sample on screen at `seconds`: the last one whose composition
 * time has arrived. Not the nearest — a frame that has not been shown yet is the
 * wrong answer to "what is playing now", however close it is.
 *
 * `samples` must be ascending by `cts`. Pure.
 */
export const sampleIndexAt = (samples, seconds, timescale, offsetSec) => {
  let low = 0;
  let high = samples.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (secOf(samples[mid], timescale, offsetSec) <= seconds) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return found < 0 ? 0 : found;
};

/**
 * The samples whose bytes lie inside `chunk`, in decode order. A chunk starts at
 * a keyframe, so this run decodes on its own — which is what lets chunks be
 * fetched and decoded in whatever order the viewer needs them. Pure.
 */
export const samplesInChunk = (samples, chunk) =>
  samples.filter(
    (sample) => sample.offset >= chunk.start && sample.offset < chunk.end,
  );

/**
 * Decode one chunk and hand out the frame playing at each moment in
 * `seconds`, through `onFrame(second, blob)`.
 *
 * `seconds` are the strip's own moments, not the video's frame times: each is
 * resolved to the sample actually on screen then, so a frame lands under the
 * tile that asked for it. Moments outside the chunk are ignored, and two landing
 * on the same source frame collapse.
 *
 * Everything else in the chunk goes through the decoder as reference and is
 * closed without a canvas — measured, that is the difference between 250
 * canvases and eight.
 */
export const decodeChunk = async ({
  table,
  store,
  chunk,
  seconds,
  height,
  signal,
  onFrame,
}) => {
  if (!canUseWebCodecs()) throw new Error('no webcodecs');
  const { track, samples, aspect, offsetSec } = table;
  const run = samplesInChunk(samples, chunk);
  if (!run.length || !seconds.length) return;

  // Which sample is on screen at each moment, mapped back to the moment that
  // asked — the strip stores frames by the second they stand for, not by where
  // the video happens to have put a frame.
  const askedBy = new Map();
  seconds.forEach((second) => {
    const at = sampleIndexAt(samples, second, track.timescale, offsetSec);
    const stamp = stampOf(samples[at], track.timescale);
    if (!askedBy.has(stamp)) askedBy.set(stamp, second);
  });
  const wanted = new Set(askedBy.keys());
  if (!wanted.size) return;

  // Waited for BEFORE the decoder is built, and given back in the `finally`
  // below however this ends, so a throw part way cannot strand a slot.
  await acquireDecoder();
  const decoder = createFrameDecoder({
    track,
    height,
    aspect,
    offsetSec,
    wanted,
    onFrame: (frameSec, blob, stamp) => onFrame(askedBy.get(stamp), blob),
  });

  try {
    for (let at = 0; at < run.length; at += 1) {
      if (signal?.aborted) break;
      const sample = run[at];
      // A whole GOP is up to a couple of hundred samples. Queued in one
      // uninterrupted burst they overwhelm the codec AND starve the output
      // callbacks that would free its buffers, so the loop yields when it is full.
      // eslint-disable-next-line no-await-in-loop
      await decoder.ready();
      decoder.push(
        sample,
        store.slice(sample.offset, sample.offset + sample.size),
      );
    }
    // An aborted chunk is dropped rather than drained: its frames are for a
    // caller that has gone away, so making the decoder emit them all is work
    // nobody is waiting for.
    if (signal?.aborted) decoder.discard();
    else await decoder.finish();
  } finally {
    releaseDecoder();
  }
};

/**
 * A decoder open for a known set of keyframes, taking each one's bytes as they
 * arrive rather than after all of them have.
 *
 * A keyframe stands alone, so this is the cheapest frame the video can give:
 * measured, one is 170.9 KB against 1.5MB for the run it sits at the head of.
 * That is what fills a coarse strip quickly — at Fit zoom a tile spans 66s and
 * cannot show the difference between a keyframe and the second beside it.
 *
 * Open it before fetching, not after. Waiting for the whole set turns a strip
 * that fills in over a couple of seconds into one that shows nothing for a
 * couple of seconds and then appears at once, which reads as a far longer wait
 * than it is.
 *
 * Async because it waits for a decoder slot (MAX_LIVE_DECODERS). The slot is held
 * until `finish()`, which is the whole time this decoder is open — including the
 * network waits between pushes, since the point of it is to decode as bytes land.
 */
export const openKeyframeDecoder = async ({
  table,
  keyframes,
  height,
  onFrame,
}) => {
  if (!canUseWebCodecs()) throw new Error('no webcodecs');
  const { track, aspect, offsetSec } = table;
  // Report each frame at the caller's own idea of when it is, not at the
  // decoder's. A chunk timestamp is whole microseconds, so reading a second back
  // out of it lands a hair off the value the caller computed from `cts` — close
  // enough to look right on screen, and not equal, so a caller checking whether
  // it already holds that moment always decides it does not and fetches it
  // again.
  const secondOf = new Map();
  keyframes.forEach((sample) => {
    secondOf.set(
      stampOf(sample, track.timescale),
      secOf(sample, track.timescale, offsetSec),
    );
  });
  await acquireDecoder();
  const decoder = createFrameDecoder({
    track,
    height,
    aspect,
    offsetSec,
    wanted: new Set(secondOf.keys()),
    onFrame: (second, blob, stamp) => onFrame(secondOf.get(stamp), blob),
  });
  // The slot goes back whichever way the caller lets go — `finish()` or
  // `discard()` — and only once however many times they are called, since a slot
  // released twice would raise the ceiling.
  let released = false;
  const giveBackSlot = () => {
    if (released) return;
    released = true;
    releaseDecoder();
  };
  return {
    ...decoder,
    finish: async () => {
      try {
        return await decoder.finish();
      } finally {
        giveBackSlot();
      }
    },
    discard: () => {
      try {
        decoder.discard();
      } finally {
        giveBackSlot();
      }
    },
  };
};

/**
 * Index of the frame within `tolerance` of `seconds`, or -1 when none is.
 *
 * `frameIndexAt` already picks the nearest; this adds whether nearest is near
 * ENOUGH, which is what separates "this tile has its own frame" from "this tile
 * is borrowing one" — and borrowing is what makes a strip flicker as closer
 * frames arrive. Pure.
 */
export const frameWithinAt = (times, seconds, tolerance) => {
  if (!times || !times.length) return -1;
  const at = frameIndexAt(times, seconds);
  return Math.abs(times[at] - seconds) <= tolerance ? at : -1;
};
